import { Box, Checkbox, MenuItem, Select, Typography } from "@material-ui/core";
import { green, orange } from "@material-ui/core/colors";
import { get } from "lodash";
import React, { useState } from "react";
import {
	Datagrid,
	FormDataConsumer,
	Pagination as RaPagination,
	ReferenceManyField,
	SelectInput,
	TextField,
	TextInput,
	useInput,
	useQueryWithStore,
	useTranslate,
} from "react-admin";

const BookmakerField = ({ source, record }) => (
	<Box style={{ textAlign: "center" }}>
		<img
			style={{ width: 100 }}
			alt={get(record, source)}
			src={get(record, "bookmaker.logo.path")}
		/>
		<Typography variant="body2">{get(record, source)}</Typography>
	</Box>
);
const BookmakerDescriptionField = ({ source, record }) => {
	const translate = useTranslate();
	return (
		<>
			<Typography
				variant="body1"
				dangerouslySetInnerHTML={{ __html: get(record, source) }}
			/>
			{get(record, "bookmaker.status") === "paused" && (
				<Typography variant="body2">
					{translate("resources.bonus-comparators.messages.bookmaker_paused")}
				</Typography>
			)}
		</>
	);
};

const Pagination = ({ addLabel, ...props }) => <RaPagination {...props} />;

const SelectionInput = ({ items, record, onChange }) => {
	if (!record || !items) {
		return null;
	}
	const selection = items.find(
		(item) => item.bookmaker_channel_id === record.id
	);
	const selectable =
		selection ||
		!items.find((item) => item.bookmaker_id === record.bookmaker_id);
	if (!selectable) {
		return null;
	}
	const handleCheckboxChange = (evt) => {
		let newItems = evt.target.checked
			? items.concat([
					{
						bookmaker_channel_id: record.id,
						bookmaker_id: record.bookmaker_id,
						rating: 1,
					},
			  ])
			: items.filter((i) => i.bookmaker_channel_id !== record.id);
		onChange(newItems);
	};
	const handleRatingChange = (evt) => {
		if (!selection) {
			return;
		}
		onChange(
			items.map((item) =>
				item.bookmaker_channel_id === record.id
					? { ...item, rating: evt.target.value }
					: item
			)
		);
	};
	return (
		<Box alignContent="center" alignItems="center" textAlign="center">
			<Checkbox
				checked={selection !== undefined}
				onChange={handleCheckboxChange}
			/>
			{selection && <Typography variant="body2">Rating</Typography>}
			{selection !== undefined && (
				<Select
					variant="outlined"
					disabled={selection === undefined}
					value={selection.rating}
					onChange={handleRatingChange}
				>
					{[1, 2, 3, 4, 5].map((v) => (
						<MenuItem key={v} value={v}>
							{v}
						</MenuItem>
					))}
				</Select>
			)}
		</Box>
	);
};

// In questo momento persiste un problema sul SORT all'interno del ReferenceManyField.
// https://github.com/marmelab/react-admin/pull/5094/files/2899558ed3bc3eb728204ce99754e6b906ac285c#diff-4b24b3f7ec6d6e722025f82956066ebf
// C'è un bug aperto, qualcuno ha trovato la soluzione ma Frank Panzerotto la sta valutando e gli suggerisce modifiche, appena sarà rilasciata
// il sistema di paginazione e sorting riprenderà a funzionare correttamente.

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const simpleMemoize = (fn) => {
	let lastArg;
	let lastResult;
	return async (evt) => {
		let value = evt.target.value;
		if (value !== lastArg) {
			lastArg = value;
			await sleep(500);
			if (lastArg === value) {
				lastResult = fn(value);
			}
		}
		return lastResult;
	};
};

const rowStyle = (record) => {
	const bookmakerStatus = get(record, "bookmaker.status", "none");
	return {
		borderLeftWidth: 4,
		borderLeftColor: bookmakerStatus === "active" ? green[100] : orange[100],
		borderLeftStyle: "solid",
		marginLeft: -4,
		paddingLeft: -4,
	};
};

const ItemsInput = ({ source, record, addLabel, ...props }) => {
	const translate = useTranslate();
	const [filters, setFilters] = useState({
		bookmaker_id: undefined,
		bc_enabled: true,
		q: undefined,
	});
	const {
		input: { onChange },
	} = useInput({
		source,
		record,
		...props,
	});
	const { loaded, data } = useQueryWithStore({
		type: "getList",
		resource: "bookmakers",
		payload: {
			sort: { field: "name", order: "ASC" },
			pagination: {
				page: 1,
				perPage: 250,
			},
			filter: {
				bookmaker_type_id: get(record, "bookmaker_type_id"),
				used_by_bookmaker_channels: true,
				bc_enabled: true,
			},
		},
	});
	return (
		<Box>
			<Typography variant="body1">
				{translate("resources.bonus-comparators.fields.items.help")}
			</Typography>
			<Typography variant="body2" style={{ marginBottom: 16 }}>
				{translate("resources.bonus-comparators.fields.items.help_links")}
			</Typography>
			<SelectInput
				source="bookmaker_id"
				record={filters}
				choices={[
					{
						id: null,
						name: "resources.bonus-comparators.fields.all_bookmakers",
					},
				].concat(loaded ? data : [])}
				optionText="name"
				onChange={(evt) =>
					setFilters({ ...filters, bookmaker_id: evt.target.value })
				}
			/>{" "}
			<TextInput
				source="q"
				label={translate("resources.bonus-comparators.fields.q")}
				record={filters}
				onChange={simpleMemoize((q) => setFilters({ ...filters, q }))}
			/>
			<br />
			<div style={{ overflowX: "auto" }}>
				<FormDataConsumer {...props}>
					{({ formData, ...rest }) => (
						<ReferenceManyField
							{...props}
							record={record}
							addLabel={false}
							source="bookmaker_type_id"
							target="bookmaker_type_id"
							reference="channels"
							perPage={10}
							filter={filters}
							pagination={<Pagination />}
						>
							<Datagrid rowStyle={rowStyle}>
								<BookmakerField source="bookmaker.name" />
								<BookmakerDescriptionField
									source="bookmaker.description"
									sortable={false}
									label={translate(
										"resources.bonus-comparators.fields.items.bookmaker.description"
									)}
								/>
								<TextField source="name" />
								<SelectionInput
									label={translate(
										"resources.bonus-comparators.fields.selection"
									)}
									items={get(formData, source)}
									onChange={onChange}
								/>
							</Datagrid>
						</ReferenceManyField>
					)}
				</FormDataConsumer>
			</div>
		</Box>
	);
};

export default ItemsInput;
